/**
 * NotificationBell — header icon, live unread badge, pulse on arrival.
 *
 * Clicking opens the side drawer (NotificationDrawer).  The badge listens
 * to `useNotifications().unread` for real-time updates via SSE.  A small
 * pulse animation fires on each fresh `cs-notification` window event.
 */
import { useEffect, useState, useRef } from 'react';
import { BellRinging, BellSlash } from '@phosphor-icons/react';
import { useNotifications } from './NotificationProvider';

const NotificationBell = ({ onOpen, dataTestId = 'notification-bell' }) => {
  const { unread, connected } = useNotifications();
  const [pulse, setPulse] = useState(false);
  const pulseTimer = useRef(null);

  useEffect(() => {
    const onArrival = () => {
      setPulse(true);
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(() => setPulse(false), 1200);
    };
    window.addEventListener('cs-notification', onArrival);
    return () => {
      window.removeEventListener('cs-notification', onArrival);
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
    };
  }, []);

  const display = unread > 99 ? '99+' : String(unread);

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={dataTestId}
      aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
      className="relative p-2 rounded-xl transition-colors hover:bg-[var(--cs-bg-hover)]"
      style={{ color: 'var(--cs-text-muted)' }}
    >
      {connected ? (
        <BellRinging size={20} weight={unread > 0 ? 'fill' : 'regular'}
          style={{ color: unread > 0 ? 'var(--cs-accent, #CD040B)' : undefined }} />
      ) : (
        <BellSlash size={20} />
      )}
      {unread > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black flex items-center justify-center tabular-nums cs-notif-badge"
          data-pulse={pulse ? '1' : '0'}
          data-testid="notification-bell-badge"
        >
          {display}
        </span>
      )}
      {/* Pulse ring on arrival */}
      {pulse && (
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-xl pointer-events-none"
          style={{
            boxShadow: '0 0 0 0 rgba(205,4,11,0.6)',
            animation: 'cs-bell-ring 1.2s ease-out',
          }}
        />
      )}
    </button>
  );
};

export default NotificationBell;
